import React from 'react';
import PropTypes from 'prop-types';
import { FormRow, Input, ExpandFormRow } from '@here/ref-client-ui';
import set from 'lodash/set';
import isFinite from 'lodash/isFinite';
import get from 'lodash/get';
import './inputObjectFormView.scss';
import { getOpenapiUrl, getOpenapiInfo } from '../openapiHelpers';
import Searchable from '../../../views/shared/searchUtils/Searchable';

function convertString2Object(value) {
  const result = {};
  if (!value) {
    return result
  }
  value.split(',').forEach((pair)=>{
    const [k,v] = pair.split(':');
    if (k) {
      result[k] = parseInt(v);
    }
  })
  return result;
}

function convertObject2String(v) {
  if (!v) {
    return ''
  }
  return Object.keys(v).map((k)=>`${k}:${v[k]}`).join(',');
}

class InputObjectFormView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      openapiInfo: null,
    };
  }
  componentDidMount() {
    const {
      options: { openapiInfo },
      settings,
      fields: { customizations }
    } = this.props;
    
    if (openapiInfo) {
      const { urlPath, urlRegex, urlSuffix, keys } = openapiInfo;
      const propsUrl = get(this.props, urlPath);
      const url = getOpenapiUrl(propsUrl, urlRegex, urlSuffix);
      getOpenapiInfo(url, keys, customizations, settings).then((openapiInfo) => {
        if (!this.isUnmounted) {
          this.setState({ openapiInfo });
        }
      });
    }
  }

  componentDidUpdate() {
    const {
      options: { key, removeSpaces },
      fields,
      setFields,
    } = this.props;
    
    const field = fields[key];
    if (removeSpaces && field) {
      const noSpaces = field.replace(/\s/g, '');
      if (noSpaces !== field) {
        setFields({ [key]: noSpaces });
      }
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  onChange = ({ target: { value, dataset } }) => {
    const {
      setFields,
      fields,
      options: { key, inputType },
    } = this.props;
    
    let v = convertString2Object(fields[key]);
    if (inputType === 'number' && isFinite(+value) && value !== '') {
      v[dataset.key] = +value;
    } else if (value === '') {
      delete v[dataset.key];
    } else {
      v[dataset.key] = value;
    }
      
    v = convertObject2String(v);

    setFields({ [key]: v });
  };

  getTooltip = () => {
    const {
      options: { tooltip },
    } = this.props;
    let tooltipEl = null;
    const { openapiInfo } = this.state;
    if (tooltip || openapiInfo) {
      tooltipEl = (
        <div className="rf-object-form-view__tooltip">{tooltip || openapiInfo}</div>
      );
    }

    return tooltipEl;
  };

  isValid = () => {
    const {
      options: { key, validation = [] },
      fields,
    } = this.props;
    const value = fields[key];

    if (!value) {
      return true;
    }

    for (let i = 0; i < validation.length; i++) {
      const validationConfig = validation[i];
      switch (validationConfig.type) {
        case 'min': {
          if (+value < validationConfig.value) {
            return false;
          }
          break;
        }
        case 'max': {
          if (+value > validationConfig.value) {
            return false;
          }
          break;
        }
        case 'regExp': {
          let re = new RegExp(validationConfig.value);
          if (!re.test(value)) {
            return false;
          }
          break;
        }
        default:
          console.error(`Unsupported validation type: "validationConfig.type"`);
          return false;
      }
    }
    return true;
  };

  render() {
    const {
      options: { key, label, inputType, isExpanded, inputs },
      fields,
    } = this.props;
    
    let group = convertString2Object(fields[key]);
    const inputsEle = inputs.map((input, index) => {
      let v = group[input.key];
        return <FormRow 
          key={key+index}
          className="rf-object-form-view"
          >
          <Input
            data-key={input.key}
            value={v}
            onBlur={this.onChange}
            label={input.label}
            placeholder={input.placeholder || input.label}
            type={inputType || 'text'}
            isValid={this.isValid()}
            tooltipPlacement="right"
            blurOnEnter
          />
        </FormRow>
      });

    return (
      <Searchable searchKey={key}>
        <ExpandFormRow 
          className="rf-object-form-view"
          isExpanded={!!isExpanded}
          tooltip={this.getTooltip()}
          label={label}
          >
          {inputsEle}
        </ExpandFormRow>
      </Searchable>
    );
  }
}

InputObjectFormView.propTypes = {
  fields: PropTypes.object,
  options: PropTypes.object,
  setFields: PropTypes.func.isRequired,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  parseUrl: function ({
    parsedParams,
    options,
    hostAndPath,
    settings,
    postData = {},
  }) {
    const { key, isPathParam, isPost } = options;
    const keyLowerCase = key.toLowerCase();
    if (isPathParam) {
      const regex = new RegExp(`{${key}}`);
      const pathParamIndex = settings.url
        .split('/')
        .findIndex((pathParam) => regex.test(pathParam));
      return { [key]: hostAndPath.split('/')[pathParamIndex] };
    } else if (isPost) {
      const value = get(postData, key, '');
      return { [key]: convertObject2String(value) };
    }
    let res = { [key]: parsedParams[keyLowerCase] || '' };
    delete parsedParams[keyLowerCase];

    return res;
  },
  defaultState: (options) => ({
    [options.key]: options.value || '',
  }),
  getRequestOptions: (fields, options) => {
    const { key, isPost, isPathParam } = options;
    if (isPathParam) {
      return {
        pathParams: { [key]: fields[key] },
      };
    }
    
    if (isPost) {
      const data = {};
      if (fields[key] !== '' && fields[key] != null) {
        const v = convertString2Object(fields[key]);
        set(data, key, v);
      }
      return {
        method: 'post',
        data,
      };
    }
    
    let params = (fields[key] || fields[key]===0) ? { [key]: fields[key] } : {};
    if (params[key] && typeof params[key] === 'object') {
      params[key] = Object.keys(params[key]).map((k)=>`${k}:${params[key][k]}`).join(',');
    }
    return { params };
  },
  Component: InputObjectFormView,
};
